import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  TextField,
  Typography,
  useTheme,
  Stack
} from "@mui/material";
import { ProgressButton, SquareIconButton, useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { usePartiallyCompleteMutation } from "~/redux/public/picks.hooks";
import { selectUsersClientId } from "~/redux/selectors/authSelectors";
import { selectPickingStateArgs } from "~/redux/selectors/autostoreSelectors";
import {
  selectAbleQtyToPick,
  selectOutOfStockReasonCode
} from "~/redux/selectors/outOfStockSelectors";
import { selectUsersFulfillmentCenterId } from "~/redux/selectors/storeSelectors";
import {
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import {
  useGetFocusedPickQuery,
  useGetBatchPickingStateQuery,
  useGetTotesForBatchQuery
} from "~/redux/warehouse/autostorePicking.hooks";

import { useSplitPickMutation } from "~/redux/warehouse/pick.hooks";

import { useAddToteMutation } from "~/redux/warehouse/totes.hooks";

import { closeAddToteModal } from "./addToteModal.slice";
import { useRefetchBatchPickingState } from "./hooks/useRefetchBatchPickingState";
import { setAbleToPickQty } from "./outOfStockDialog/outOfStockDialog.slice";

type AddToteModalBodyProps = {
  maxValue: number;
  pickQty: number;
  handleConfirm: (value: number | undefined) => Promise<void>;
};

export const AddToteModalBody = (props: AddToteModalBodyProps) => {
  const { maxValue, pickQty, handleConfirm } = props;
  const { t } = useTranslation();
  const { palette } = useTheme();
  const trackedModalName = useMixpanelModalName("Add Tote");

  const [amountInNewTote, setAmountInNewTote] = useState<number>(maxValue);

  const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAmountInNewTote((event.target.value && Number(event.target.value)) || 0);
  };

  const handleArrowClick = (increment: number): void => {
    // ensure the incremented field value change remains within the bounds of [1,maxMoveQty]
    setAmountInNewTote(
      Math.max(1, Math.min((amountInNewTote || 0) + increment, maxValue))
    );
  };

  return (
    <Box sx={{ marginTop: 1 }}>
      <Typography sx={{ fontSize: "1.75em" }}>
        {`${t("how many units should be")} `}
        <span style={{ color: palette.success.main }}>{`${t("moved")}`}</span>
        {` ${t("into the new tote")}?`}
      </Typography>
      <Stack
        direction="row"
        gap={2}
        justifyContent="center"
        alignItems="center"
        my={4}
      >
        <TextField
          id="quantity"
          style={{ fontSize: "2em" }}
          variant="outlined"
          value={amountInNewTote}
          onChange={handleQuantityChange}
          onBlur={() => {
            if (amountInNewTote > 0) {
              mixpanelTrack({
                trackedPageName: trackedModalName,
                type: "Input Change",
                label: "Enter Quantity",
                eventProperties: {
                  Quantity: amountInNewTote
                }
              });
            }
          }}
          inputProps={{
            style: {
              width: "100px",
              height: "115px",
              fontSize: "6em",
              textAlign: "center"
            },
            "aria-label": "quantity-textbox"
          }}
          InputLabelProps={{
            shrink: true
          }}
        />
        <Stack
          direction="row"
          gap={0}
          justifyContent="center"
          alignItems="center"
        >
          <Typography
            sx={{
              fontSize: "6em",
              fontWeight: 200,
              textAlign: "center"
            }}
          >
            /
          </Typography>
          <Typography
            sx={{
              fontSize: "6em",
              textAlign: "center"
            }}
            aria-label="pick amount"
          >
            {pickQty}
          </Typography>
        </Stack>
        <Stack
          direction="column"
          gap={2}
          justifyContent="center"
          marginLeft={2}
        >
          <SquareIconButton
            aria-label="increment"
            onClick={() => {
              if (amountInNewTote < maxValue) {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Button Click",
                  label: "Increase Quantity",
                  eventProperties: {
                    Quantity: amountInNewTote + 1
                  }
                });
              }
              handleArrowClick(1);
            }}
            size="large"
          >
            <ExpandLessIcon sx={{ fontSize: "27px" }} />
          </SquareIconButton>
          <SquareIconButton
            onClick={() => {
              if (amountInNewTote > 1) {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  type: "Button Click",
                  label: "Decrease Quantity",
                  eventProperties: {
                    Quantity: amountInNewTote - 1
                  }
                });
              }
              handleArrowClick(-1);
            }}
            aria-label="decrement"
            size="large"
          >
            <ExpandMoreIcon sx={{ fontSize: "27px" }} />
          </SquareIconButton>
        </Stack>
      </Stack>

      <Box width="100%" display="flex" justifyContent="flex-end">
        <ProgressButton
          aria-label="confirm-button"
          onClick={() => {
            if (trackedModalName) {
              mixpanelTrack({
                trackedPageName: trackedModalName,
                type: "Button Click",
                label: "Confirm",
                eventProperties: {
                  Quantity: amountInNewTote
                }
              });
            }
            void handleConfirm(amountInNewTote);
          }}
          buttonSize="medium"
          color="primary"
        >
          {t("confirm")}
        </ProgressButton>
      </Box>
    </Box>
  );
};

function AddToteModal() {
  const [splitPick] = useSplitPickMutation();
  const [partiallyCompletePick] = usePartiallyCompleteMutation();
  const [addTote] = useAddToteMutation();
  const { t } = useTranslation();
  const { errorToast } = useToast();

  const dispatch = useAppDispatch();
  const trackedModalName = useMixpanelModalName("Add Tote");

  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const workstationId = useAppSelector(selectWorkstationId);
  const ableToPickQty = useAppSelector(selectAbleQtyToPick);
  const clientId = useAppSelector(selectUsersClientId);
  const fulfillmentCenterId = useAppSelector(selectUsersFulfillmentCenterId);
  const outOfStockReasonCode = useAppSelector(selectOutOfStockReasonCode);

  const pickingStateArgs = useAppSelector(selectPickingStateArgs);
  useGetBatchPickingStateQuery(pickingStateArgs);
  const refetchPickingState = useRefetchBatchPickingState();

  const isOpen = useAppSelector((state) => state.addToteModal.isOpen);

  const { data: focusedPick, isLoading: isLoadingFocusedPick } =
    useGetFocusedPickQuery(workstationId ? { workstationId } : skipToken);

  const { toteContents, isLoadingToteContents } = useGetTotesForBatchQuery(
    focusedPick ? { batchId: focusedPick.batchId } : skipToken,
    {
      selectFromResult: ({ data, isLoading }) => ({
        toteContents: data
          ?.flatMap((totesAtPosition) => totesAtPosition.totes)
          ?.find((tote) => tote.toteId === focusedPick?.assignedToteId)
          ?.contents,
        isLoadingToteContents: isLoading
      })
    }
  );

  const pickQty = ableToPickQty || focusedPick?.quantity.value || 0;

  // if the current tote has contents, then we can move the full amount.
  // else, we need to reserve at least one for the current tote.
  const maxMoveQty = focusedPick
    ? toteContents?.some((ct) => ct.quantity.value > 0)
      ? pickQty
      : pickQty - 1
    : 0;

  // prevent display of intermediary states
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    void (async () => {
      if (focusedPick?.status.toLowerCase() === "canceled") {
        dispatch(closeAddToteModal());
        await refetchPickingState();
        dispatch(setAbleToPickQty(null));
      }
    })();
  }, [dispatch, focusedPick, refetchPickingState]);

  const handleConfirm = useCallback(
    async (qtyToMove: number | undefined) => {
      setIsLoading(true);

      // validation
      if (!qtyToMove || qtyToMove <= 0) {
        errorToast(`Some quantity must be moved to the new ${t("tote")}`, {
          trackingLocation: trackedModalName
        });
        setIsLoading(false);
        return;
      }

      if (qtyToMove > maxMoveQty) {
        errorToast(
          `You may not move more than ${maxMoveQty} to the new ${t("tote")}`,
          {
            trackingLocation: trackedModalName
          }
        );
        setIsLoading(false);
        return;
      }

      const toteId = focusedPick?.assignedToteId;

      if (
        focusedPick &&
        toteId &&
        workstationId &&
        clientId &&
        fulfillmentCenterId &&
        autostoreGridId
      ) {
        const totalPickQty = ableToPickQty || focusedPick.quantity.value;
        const currentToteQty = totalPickQty - qtyToMove;

        try {
          // if we need to pick some to the current tote before adding the new tote, split that off and complete it
          // before calling addTote
          if (qtyToMove < focusedPick.quantity.value) {
            const splitPickResponse = await splitPick({
              pickId: focusedPick.pickId,
              splitQuantity: {
                value: qtyToMove,
                units: focusedPick.quantity.units
              },
              batchId: focusedPick.batchId
            }).unwrap();

            // don't do anything with the "split" side of the pick. this will be the new focused pick and have a new
            // tote ID after addTote is executed.
            const currentTotePick = splitPickResponse.remainingPick;

            await partiallyCompletePick({
              pickId: currentTotePick.pickId,
              clientId,
              fulfillmentCenterId,
              requestBody: {
                autostoreGridId,
                holdReasonCode: outOfStockReasonCode,
                quantity: currentToteQty,
                pickBinId: currentTotePick.pickBin?.binId || "",
                workstationId
              }
            });
          }

          // close the modal before updating state so there isn't a refresh flicker
          dispatch(closeAddToteModal());

          // create the new tote. this moves all picks to the new tote ID
          await addTote({ toteId, workstationId });
        } catch (err) {
          errorToast(getMessageFromRtkError(err), {
            trackingLocation: trackedModalName
          });
        }
      }
      setIsLoading(false);
    },
    [
      ableToPickQty,
      dispatch,
      errorToast,
      focusedPick,
      maxMoveQty,
      t,
      workstationId,
      addTote,
      autostoreGridId,
      clientId,
      fulfillmentCenterId,
      outOfStockReasonCode,
      partiallyCompletePick,
      splitPick,
      trackedModalName
    ]
  );

  return (
    <Dialog
      key="autostore-add-tote-dialog"
      open={isOpen}
      onClose={() => dispatch(closeAddToteModal())}
      sx={{ padding: 0, margin: 0, fontFamily: "Roboto" }}
      aria-labelledby="autostore-add-tote"
    >
      <DialogContent>
        <DialogContentText
          variant="h2"
          sx={{ color: "#000", fontWeight: "bold", fontSize: "2.3em" }}
          id="autostore-add-tote"
        >
          {t("add tote")}
        </DialogContentText>
        {isLoading || isLoadingToteContents || isLoadingFocusedPick ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <AddToteModalBody
            pickQty={pickQty}
            maxValue={maxMoveQty}
            handleConfirm={handleConfirm}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AddToteModal;
